import { usePMAuth } from '@/contexts/PMAuthContext';
import { useNavigate } from 'react-router-dom';
import { CreditCard, LogOut, User, ChevronDown } from 'lucide-react';
import { SCAFreshnessChip } from '@/components/SCAFreshnessChip';
import { PM_SCA_KEY } from '@/hooks/useSCAFreshness';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { PMNotificationsBell } from './PMNotificationsBell';

export function PMHeader() {
  const { pmUser, programs, signOut } = usePMAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/pm/login', { replace: true });
  };

  return (
    <header className="h-14 border-b border-border/50 bg-card/60 backdrop-blur-sm px-4 flex items-center justify-between shrink-0">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
            <CreditCard className="w-4 h-4 text-accent" />
          </div>
          <div>
            <h1 className="text-sm font-semibold text-foreground leading-none">
              Program Manager Portal
            </h1>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              {programs.length > 0 ? programs[0]?.name || 'Program' : 'No programs'}
            </p>
          </div>
        </div>
        {programs.length > 1 && (
          <Badge variant="secondary" className="text-[9px]">
            +{programs.length - 1} more
          </Badge>
        )}
      </div>

      <div className="flex items-center gap-2">
        <SCAFreshnessChip storageKey={PM_SCA_KEY} />
        <PMNotificationsBell />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-2 text-sm">
              <div className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center">
                <User className="w-3 h-3 text-accent" />
              </div>
              <span className="text-muted-foreground max-w-[160px] truncate">
                {pmUser?.email || 'User'}
              </span>
              <ChevronDown className="w-3 h-3 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="px-3 py-2">
              <p className="text-sm font-medium text-foreground">{pmUser?.email}</p>
              <p className="text-xs text-muted-foreground capitalize">{pmUser?.role}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
